/**
 * LSG06: PLP – Bestsellers-First Shop All with Category Sections
 *
 * Sticky category sub-nav for the Shop All variation. The active pill tracks
 * whichever category row is currently under the header as you scroll — the
 * scroll-linked behaviour the Figma prototype could not show.
 *
 * The variation itself is revealed by the .lsg06-variation body class that
 * Convert Experiences injects; this file only drives the nav behaviour.
 */
(function () {
  var VARIATION_CLASS = 'lsg06-variation';

  // QA hook: ?lsg06=1 forces the variation on for a test-only audience,
  // per the Split Test SOP requirement to QA live rather than from a preview.
  try {
    if (new URLSearchParams(window.location.search).get('lsg06') === '1') {
      document.body.classList.add(VARIATION_CLASS);
    }
  } catch (e) {
    // URLSearchParams unsupported — QA falls back to the Convert audience.
  }

  /**
   * Flickity measures cells on init. If Convert adds the body class after the
   * carousels have already initialised inside a display:none container, every
   * cell is zero-width, so re-measure once the variation becomes visible.
   */
  function resizeCarousels() {
    if (typeof Flickity === 'undefined') return;

    document.querySelectorAll('.shopall-categories slide-show').forEach(function (slideshow) {
      var instance = Flickity.data(slideshow);
      if (instance) instance.resize();
    });
  }

  if (!document.body.classList.contains(VARIATION_CLASS)) {
    var observer = new MutationObserver(function () {
      if (!document.body.classList.contains(VARIATION_CLASS)) return;
      observer.disconnect();
      resizeCarousels();
      initCarousels();
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  }

  /**
   * The theme drives .flickity-progress--bar as a fill, widening it from 0% to
   * 100% as you scroll. The design wants a scrollbar thumb — fixed width,
   * sliding along the track — so take the bar over: size it to the share of the
   * row that's visible and move it with translateX instead.
   *
   * The theme's own handler still runs and still sets width; ours is bound
   * afterwards, so it has the last word on every scroll event.
   */
  function initCarousel(carousel) {
    if (typeof Flickity === 'undefined' || carousel.dataset.progressReady) return;

    var slideshow = carousel.querySelector('slide-show');
    var bar = carousel.querySelector('.flickity-progress--bar');
    var prev = carousel.querySelector('.flickity-prev');
    var next = carousel.querySelector('.flickity-next');
    if (!slideshow || !bar) return;

    var flkty = Flickity.data(slideshow);
    if (!flkty) return;

    carousel.dataset.progressReady = 'true';

    var progress = 0;

    function render(value) {
      if (typeof value === 'number') progress = Math.max(0, Math.min(1, value));

      var visible = flkty.size && flkty.size.innerWidth;
      var total = flkty.cells.reduce(function (sum, cell) {
        return sum + cell.size.outerWidth;
      }, 0);
      if (!visible || !total) return;

      var ratio = Math.min(1, visible / total);

      bar.style.width = ratio * 100 + '%';
      // translateX percentages resolve against the thumb's own width, so the
      // full run of the track is (1 / ratio - 1) thumb-widths.
      bar.style.transform = 'translateX(' + progress * (100 / ratio - 100) + '%)';

      // wrapAround is off whenever a progress bar is present, so the ends are
      // real dead ends worth showing.
      if (prev) prev.classList.toggle('is-disabled', progress <= 0.001);
      if (next) next.classList.toggle('is-disabled', progress >= 0.999);
    }

    flkty.on('scroll', render);
    window.addEventListener('resize', function () {
      render();
    }, { passive: true });

    render(0);
  }

  function initCarousels() {
    document.querySelectorAll('.shopall-categories__carousel').forEach(initCarousel);
  }

  // slideshow.js is deferred and loads after this file, so Flickity doesn't
  // exist yet — wait for the document to finish parsing before reaching for it.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousels);
  } else {
    initCarousels();
  }

  document.addEventListener('shopify:section:load', initCarousels);
})();

if (!customElements.get('shopall-category-nav')) {
  class ShopallCategoryNav extends HTMLElement {
    connectedCallback() {
      this.links = Array.from(this.querySelectorAll('.shopall-categories__pill'));
      this.scroller = this.querySelector('.shopall-categories__nav-scroller');

      this.rows = this.links
        .map((link) => document.getElementById(link.dataset.target))
        .filter(Boolean);

      if (!this.rows.length) return;

      this.ticking = false;
      this.onScroll = this.onScroll.bind(this);
      this.onClick = this.onClick.bind(this);

      this.links.forEach((link) => link.addEventListener('click', this.onClick));
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });

      this.setActive(0, false);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
    }

    /**
     * Where the bar pins: directly below the sticky site header. Mirrors the
     * `top` in the stylesheet, so the two can't drift apart.
     */
    get pinTop() {
      const headerHeight = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
        10
      );

      return Math.max(0, headerHeight || 0);
    }

    /**
     * Distance from the top of the viewport to below the header and this nav,
     * i.e. the line a row has to cross to count as the one being viewed.
     */
    get offset() {
      return this.pinTop + this.offsetHeight;
    }

    onClick(event) {
      const row = document.getElementById(event.currentTarget.dataset.target);
      if (!row) return;

      event.preventDefault();

      window.scrollTo({
        top: row.getBoundingClientRect().top + window.scrollY - this.offset,
        behavior: 'smooth'
      });
    }

    onScroll() {
      if (this.ticking) return;

      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.ticking = false;
        this.update();
      });
    }

    update() {
      // The active row is the last one whose top has passed under the nav. A
      // few pixels of slack keeps the pill from flickering mid-transition.
      const threshold = this.offset + 4;
      let index = 0;

      this.rows.forEach((row, i) => {
        if (row.getBoundingClientRect().top <= threshold) index = i;
      });

      this.setActive(index);
    }

    setActive(index, scrollIntoView = true) {
      const link = this.links[index];
      if (!link || link.classList.contains('is-active')) return;

      this.links.forEach((item) => item.classList.remove('is-active'));
      link.classList.add('is-active');

      if (scrollIntoView && this.scroller) {
        // Keep the active pill in view in the horizontally scrolling nav
        // without dragging the page around with it.
        const scrollerRect = this.scroller.getBoundingClientRect();
        const linkRect = link.getBoundingClientRect();

        if (linkRect.left < scrollerRect.left || linkRect.right > scrollerRect.right) {
          this.scroller.scrollTo({
            left: link.offsetLeft - this.scroller.clientWidth / 2 + link.offsetWidth / 2,
            behavior: 'smooth'
          });
        }
      }
    }
  }

  customElements.define('shopall-category-nav', ShopallCategoryNav);
}
